import {
  Icon,
  InputElement,
  InputGroup,
  NumberInputInputProps,
} from '@chakra-ui/react';
import {
  IPrice,
  IProductWithQuantityDisplayType,
  OfferingTypesEnum,
  PriceModelEnum,
  ProductQuantityDisplayTypeEnum,
  ProductTypeEnum,
} from '@monetize/types/app';
import { ExperienceConfigurationKeys } from '@monetize/types/experience-config';
import { getCurrencySymbol } from '@monetize/utils/core';
import get from 'lodash/get';
import isNumber from 'lodash/isNumber';
import { FocusEvent, useEffect, useMemo, useState } from 'react';
import {
  Controller,
  FieldErrors,
  FieldValues,
  useFormContext,
} from 'react-hook-form';
import { MdClear, MdWarningAmber } from 'react-icons/md';
import {
  MButton,
  MCustomIconButton,
  MCustomNumberInput,
  MFlex,
  MFormField,
  MLockedTextOrContent,
  MTd,
  MText,
  MTooltip,
  MTr,
} from '../../../../../components/Monetize';
import { MCustomSelect } from '../../../../../components/Monetize/MCustomSelect/MCustomSelect';
import { OFFERINGS } from '../../../../../constants';
import { useExperienceConfiguration } from '../../../../../hooks';
import { useFlags } from '../../../../../services/launchDarkly';
import { disabledFieldStyle } from '../../../../../styles/chakra/components/Input';
import { transformValueToMinimumTwoDecimalPlaces } from '../../../../../utils';
import { DisabledCustomPriceModelLabel } from './components/DisabledCustomPriceModelLable';
import { PriceModelItemContent } from './components/PriceModelItemContent';
import { TooltipOrChildren } from './components/TooltipOrChildren';
import { RatePercentOfTotalConfig } from './RatePercentOfTotalConfig';
import {
  getAmountPaddingStart,
  getInitialQuantityByProductType,
  RATE_AMOUNT_DISPLAY_FORMAT,
  RATE_AMOUNT_EDIT_FORMAT,
} from './RatePriceUtils';

/**
 * Disabled treatment for the "Up to" / "Maximum Total" fields. `∞` is the
 * meaningful "no upper bound" value there, so it has to stay legible while the
 * field is disabled — everywhere else the theme hides disabled placeholders.
 *
 * Spread the theme's `disabledFieldStyle` rather than redefining it: Chakra
 * merges style props over theme styles shallowly, so a partial `_disabled`
 * replaces the theme block wholesale and silently drops its border/background.
 */
const DISABLED_WITH_VISIBLE_PLACEHOLDER = {
  ...disabledFieldStyle,
  _placeholder: { color: 'fg.subtle' },
};

/**
 * `styleProps` for the tier number inputs. Module scope so every render hands
 * MCustomNumberInput the same object identity.
 *
 * Border, background and text color on disabled all come from the Input theme —
 * do not re-declare them here or the four columns drift apart again.
 */
const TIER_INPUT_STYLES: NumberInputInputProps = {
  fontFamily: 'sans-serif',
};

const TIER_INPUT_STYLES_WITH_PLACEHOLDER: NumberInputInputProps = {
  fontFamily: 'sans-serif',
  _disabled: DISABLED_WITH_VISIBLE_PLACEHOLDER,
};

/**
 * "Up to" gets an orange resting border alongside its warning icon when the
 * tier would be invisible in the quote quantity dropdown. Disabled still wins
 * (`:disabled` outranks the base rule), so the field greys out consistently.
 */
const TIER_INPUT_STYLES_WITH_WARNING: NumberInputInputProps = {
  ...TIER_INPUT_STYLES_WITH_PLACEHOLDER,
  borderColor: 'tOrange.tangerine',
};

const READ_ONLY_TIER_INPUT_STYLES: NumberInputInputProps = {
  _readOnly: {
    border: 'none',
    outline: 'none',
    resize: 'none',
    px: 0,
    background: 'transparent',
    color: 'tPurple.dark',
    _placeholder: { color: 'transparent' },
  },
};

const READ_ONLY_TIER_INPUT_STYLES_WITH_PLACEHOLDER: NumberInputInputProps = {
  _readOnly: {
    ...READ_ONLY_TIER_INPUT_STYLES._readOnly,
    _placeholder: { color: 'tPurple.dark' },
  },
};

const AMOUNT_INPUT_PADDING_START = 5;

const AMOUNT_INPUT_STYLES: NumberInputInputProps = {
  pl: AMOUNT_INPUT_PADDING_START,
  pr: 5,
  textAlign: 'right',
};

const READ_ONLY_AMOUNT_INPUT_STYLES: NumberInputInputProps = {
  ...AMOUNT_INPUT_STYLES,
  ...READ_ONLY_TIER_INPUT_STYLES_WITH_PLACEHOLDER,
};

const POT_AMOUNT_INPUT_STYLES: NumberInputInputProps = {
  pl: 0,
  pr: 5,
  textAlign: 'right',
};

const READ_ONLY_POT_AMOUNT_INPUT_STYLES: NumberInputInputProps = {
  ...POT_AMOUNT_INPUT_STYLES,
  ...READ_ONLY_TIER_INPUT_STYLES_WITH_PLACEHOLDER,
};

interface RatePriceTableRowProps {
  id: string;
  index: number;
  product: IProductWithQuantityDisplayType;
  offeringType: OfferingTypesEnum;
  loading?: boolean;
  isLastRow?: boolean;
  lastRowIndex: number;
  isOpen: boolean;
  isLocked?: boolean;
  keyPrefix?: string;
  isPriceDrawer?: boolean;
  isTierAdjustmentDisabled?: boolean;
  isPriceAdjustmentDisabled?: boolean;
  isPriceModelDisabled?: boolean;
  handleRemovePrice: (index: number) => void;
  generateNewPrice: (
    previousFrom: null | number,
    previousTo: null | number,
    previousPriceModel: PriceModelEnum,
    previousAmount: null | number,
    previousBlockSize: null | number,
  ) => void;
  currency: string;
  pricesLengthFromApi?: number;
  isReadOnly?: boolean;
  isBlockPricing?: boolean;
}

// Custom hook for shared state and handlers
const useRatePriceTableRowState = ({
  index,
  product,
  offeringType,
  keyPrefix = 'products.',
  currency,
  pricesLengthFromApi,
  isBlockPricingProp,
  isLastRow = false,
}: {
  index: number;
  product: IProductWithQuantityDisplayType;
  offeringType: OfferingTypesEnum;
  keyPrefix?: string;
  currency: string;
  pricesLengthFromApi?: number;
  isBlockPricingProp?: boolean;
  isLastRow?: boolean;
}) => {
  const productId = product.id!;
  const { enableBlockPricing } = useFlags();
  const { allowQuantityDropdown } = useExperienceConfiguration(
    ExperienceConfigurationKeys.quoteAndContractSettings,
  );

  const {
    watch,
    control,
    setValue,
    getValues,
    clearErrors,
    setError,
    formState: { errors, dirtyFields },
  } = useFormContext();

  const dirtyFieldsWithKeyPrefix = keyPrefix
    ? dirtyFields?.[keyPrefix.substring(0, keyPrefix.length - 1)]
    : dirtyFields;

  const fieldKeyPrefix = `${keyPrefix}${productId}.prices`;
  const errorKeyPrefix = `${fieldKeyPrefix}.${index}`;
  const productError = get(errors, errorKeyPrefix) as FieldErrors<FieldValues>;
  const values = getValues(fieldKeyPrefix);

  const watchedCurrency = watch('currency') || currency;
  const watchedFrom = watch(`${fieldKeyPrefix}.${index}.from`);
  const watchedTo = watch(`${fieldKeyPrefix}.${index}.to`);
  const watchPercentOfTotalConfig = watch(`percentOfTotalConfig`);
  const watchedPriceModel = watch(`${fieldKeyPrefix}.${index}.priceModel`);
  const watchedAmount = watch(`${fieldKeyPrefix}.${index}.amount`);
  const watchBlockSize = watch(`${fieldKeyPrefix}.${index}.blockSize`);

  const isCustomPriceModel = watchedPriceModel === PriceModelEnum.CUSTOM;
  const isUsageProductRate = product.productType === ProductTypeEnum.USAGE;
  const isMinCommitProductRate =
    product.productType === ProductTypeEnum.MIN_COMMIT;
  const isOneTimePrePaidCreditProductRate =
    product.productType === ProductTypeEnum.ONETIME_PREPAID_CREDIT;
  const isDiscountProductRate =
    product.productType === ProductTypeEnum.DISCOUNT;
  const isPoTOfferingType =
    offeringType === OfferingTypesEnum.CUSTOM_PERCENT_OF_TOTAL;
  const isBlockPricing =
    isBlockPricingProp ?? watchedPriceModel === PriceModelEnum.BLOCK;

  const currencySymbol = getCurrencySymbol(watchedCurrency);
  const shouldValidateCurrentIndexForDirty =
    pricesLengthFromApi && !(index + 1 > pricesLengthFromApi);
  const errorMessageProps = { mt: 0 };

  const showQuantityDropdownWarning =
    isLastRow &&
    product.quantityDisplayType === ProductQuantityDisplayTypeEnum.DROPDOWN &&
    allowQuantityDropdown &&
    watchedPriceModel &&
    !watchedTo;

  useEffect(() => {
    if (!values || values.length === 0) {
      return;
    }

    const isMinCommitRateUpdated =
      values[0].priceModel === PriceModelEnum.VOLUME &&
      values[0].from === 0 &&
      values[0].to === null &&
      Number(values[0].amount) === 1;

    if (
      !isMinCommitRateUpdated &&
      (isMinCommitProductRate || isOneTimePrePaidCreditProductRate)
    ) {
      setValue(fieldKeyPrefix, [
        {
          ...values[0],
          priceModel: PriceModelEnum.VOLUME,
          from: 0,
          to: null,
          amount: 1,
        },
      ]);
    }

    if (isDiscountProductRate) {
      const initialQuantity = getInitialQuantityByProductType(
        product.productType,
      );
      const discountFrom = initialQuantity !== null ? 1 : 0;
      const isDiscountRateApplied =
        values.length === 1 &&
        values[0].priceModel === PriceModelEnum.CUSTOM &&
        Number(values[0].from) === discountFrom &&
        values[0].to === null &&
        Number(values[0].amount) === 0;

      if (!isDiscountRateApplied) {
        setValue(fieldKeyPrefix, [
          {
            ...values[0],
            priceModel: PriceModelEnum.CUSTOM,
            from: discountFrom,
            to: null,
            amount: 0,
          },
        ]);
      }
    }
  }, [
    fieldKeyPrefix,
    isMinCommitProductRate,
    isOneTimePrePaidCreditProductRate,
    isDiscountProductRate,
    product.productType,
    values,
    setValue,
  ]);

  useEffect(() => {
    if (values && values.length - 1 === index && isUsageProductRate) {
      setValue(`${fieldKeyPrefix}.${index}.to`, null);
    }
  }, [index, isUsageProductRate, values, fieldKeyPrefix, setValue]);

  const handlePriceModel = (onChange: (e: any) => void) => (e: any) => {
    onChange(e);
    const value = String(e);

    if (productError?.to) {
      clearErrors(`${errorKeyPrefix}.to`);
    }

    if (value !== PriceModelEnum.BLOCK) {
      const fieldToUpdate = values[index];
      if (fieldToUpdate && fieldToUpdate.blockSize !== null) {
        setValue(`${fieldKeyPrefix}.${index}.blockSize`, null, {
          shouldDirty: true,
        });
      }
    }

    if (value === PriceModelEnum.TIERED) {
      const nextPrices = values.map((price: IPrice, idx: number) => {
        if (idx === index) {
          return price;
        }

        if (
          price.priceModel === PriceModelEnum.VOLUME ||
          price.priceModel === PriceModelEnum.CUSTOM ||
          price.priceModel === PriceModelEnum.PERCENT_OF_TOTAL
        ) {
          return {
            ...price,
            priceModel: PriceModelEnum.TIERED,
          };
        }
        return price;
      });
      setValue(fieldKeyPrefix, nextPrices, {
        shouldDirty: true,
      });
    }

    if (value !== PriceModelEnum.TIERED) {
      const tieredExistsOnValue = values.some(
        (price: IPrice) => price.priceModel === PriceModelEnum.TIERED,
      );
      if (tieredExistsOnValue) {
        if (value === PriceModelEnum.VOLUME) {
          const nextPrices = values.map((price: IPrice) => ({
            ...price,
            priceModel:
              price.priceModel === PriceModelEnum.TIERED
                ? null
                : price.priceModel,
          }));
          setValue(fieldKeyPrefix, nextPrices, {
            shouldDirty: true,
          });
        }
      }
    }

    if (value === PriceModelEnum.CUSTOM) {
      const fieldToUpdate = values[index];
      const initialQuantity = getInitialQuantityByProductType(
        product.productType,
      );
      if (fieldToUpdate) {
        setValue(
          fieldKeyPrefix,
          [
            {
              ...fieldToUpdate,
              from: initialQuantity !== null ? 1 : 0,
              to: null,
              amount: 0,
            },
          ],
          {
            shouldDirty: true,
          },
        );
      }
      if (productError?.amount) {
        clearErrors(`${errorKeyPrefix}.amount`);
      }
    }

    if (value === PriceModelEnum.PERCENT_OF_TOTAL) {
      const fieldToUpdate = values[index];
      if (fieldToUpdate) {
        setValue(
          fieldKeyPrefix,
          [
            {
              ...fieldToUpdate,
              from: 0,
              to: 1,
              amount: 1,
            },
          ],
          {
            shouldDirty: true,
          },
        );
      }
    }

    if (value === PriceModelEnum.BLOCK) {
      const fieldToUpdate = values[index];
      const initialQuantity = getInitialQuantityByProductType(
        product.productType,
      );
      if (fieldToUpdate) {
        setValue(
          fieldKeyPrefix,
          [
            {
              ...fieldToUpdate,
              from: initialQuantity !== null ? 1 : 0,
              to: null,
              blockSize: 1,
              amount: 0,
            },
          ],
          {
            shouldDirty: true,
          },
        );
      }
    }
  };

  const handleTo =
    (onChange: (e: any) => void) =>
    (valueAsString: string, valueAsNumber: number) => {
      const currentPrice = values[index];
      if (
        currentPrice?.priceModel === PriceModelEnum.BLOCK &&
        valueAsNumber !== null &&
        !isNaN(valueAsNumber) &&
        currentPrice?.blockSize
      ) {
        if (valueAsNumber % currentPrice.blockSize !== 0) {
          if (valueAsString === '') {
            setValue(`${fieldKeyPrefix}.${index}.to`, null, {
              shouldDirty: true,
            });
          } else {
            setValue(`${fieldKeyPrefix}.${index}.to`, valueAsNumber, {
              shouldDirty: true,
            });
          }

          setError(`${errorKeyPrefix}.to`, {
            type: 'validate',
            message: `Value must be a multiple of block size (${currentPrice.blockSize})`,
          });
          return;
        } else {
          if (productError?.to) {
            clearErrors(`${errorKeyPrefix}.to`);
          }
        }
      }

      if (valueAsString === '') {
        onChange(null);
      } else {
        onChange(valueAsNumber);
      }

      if (
        values.priceModel !== PriceModelEnum.CUSTOM &&
        index < values.length - 1
      ) {
        setValue(`${fieldKeyPrefix}.${index + 1}.from`, valueAsNumber + 1);
      }
    };

  const handleBlockSize =
    (onChange: (e: any) => void) => (_: string, valueAsNumber: number) => {
      onChange(valueAsNumber);
    };

  let disabledPriceModelItems: PriceModelEnum[] = [];

  if (index !== 0 && values?.[0]) {
    if (values[0].priceModel === PriceModelEnum.TIERED) {
      disabledPriceModelItems = OFFERINGS.DISABLED_TIERED_PRICE_MODEL_OPTIONS;
    } else if (values[0].priceModel === PriceModelEnum.CUSTOM) {
      disabledPriceModelItems = OFFERINGS.DISABLED_CUSTOM_PRICE_MODEL_OPTIONS;
    } else if (values[0].priceModel === PriceModelEnum.FLAT) {
      disabledPriceModelItems = OFFERINGS.DISABLED_FLAT_PRICE_MODEL_OPTIONS;
    } else if (values[0].priceModel === PriceModelEnum.VOLUME) {
      disabledPriceModelItems =
        OFFERINGS.DISABLED_FLAT_VOLUME_PRICE_MODEL_OPTIONS;
    }

    const existingPriceModels = values.map((price: IPrice) => price.priceModel);
    const hasFlatModel = existingPriceModels.includes(PriceModelEnum.FLAT);
    const hasVolumeModel = existingPriceModels.includes(PriceModelEnum.VOLUME);
    const hasTieredModel = existingPriceModels.includes(PriceModelEnum.TIERED);

    if (
      hasFlatModel &&
      hasVolumeModel &&
      !disabledPriceModelItems.includes(PriceModelEnum.TIERED)
    ) {
      disabledPriceModelItems = [
        ...disabledPriceModelItems,
        PriceModelEnum.TIERED,
      ];
    }

    if (
      hasFlatModel &&
      hasTieredModel &&
      !disabledPriceModelItems.includes(PriceModelEnum.VOLUME)
    ) {
      disabledPriceModelItems = [
        ...disabledPriceModelItems,
        PriceModelEnum.VOLUME,
      ];
    }
  }

  const priceModelList = useMemo(() => {
    if (offeringType === OfferingTypesEnum.CUSTOM_PERCENT_OF_TOTAL) {
      return OFFERINGS.PRICE_MODEL_OPTIONS_DISPLAY_STRING_POT;
    }

    return OFFERINGS.PRICE_MODEL_OPTIONS_DISPLAY_STRING.filter((option) => {
      if (!enableBlockPricing) {
        return option.value !== PriceModelEnum.BLOCK;
      }
      return true;
    });
  }, [enableBlockPricing, offeringType]);

  return {
    // Form context
    control,
    errors,
    getValues,
    setValue,
    clearErrors,
    setError,
    // Field keys
    productId,
    fieldKeyPrefix,
    errorKeyPrefix,
    productError,
    values,
    // Watched values
    watchedCurrency,
    watchedFrom,
    watchedTo,
    watchPercentOfTotalConfig,
    watchedPriceModel,
    watchedAmount,
    watchBlockSize,
    // Computed flags
    isCustomPriceModel,
    isUsageProductRate,
    isMinCommitProductRate,
    isOneTimePrePaidCreditProductRate,
    isDiscountProductRate,
    isPoTOfferingType,
    isBlockPricing,
    currencySymbol,
    shouldValidateCurrentIndexForDirty,
    showQuantityDropdownWarning,
    dirtyFieldsWithKeyPrefix,
    // Handlers
    handlePriceModel,
    handleTo,
    handleBlockSize,
    // Config
    disabledPriceModelItems,
    priceModelList,
    errorMessageProps,
  };
};

// Wrapper component that delegates to the appropriate variant
export const RatePriceTableRow = (props: RatePriceTableRowProps) => {
  const { offeringType } = props;
  const isPoTOfferingType =
    offeringType === OfferingTypesEnum.CUSTOM_PERCENT_OF_TOTAL;

  return isPoTOfferingType ? (
    <RatePriceTableRowPoT {...props} />
  ) : (
    <RatePriceTableRowStandard {...props} />
  );
};

// ============================================================================
// Standard Variant (non-PoT)
// ============================================================================
const RatePriceTableRowStandard = ({
  index,
  product,
  offeringType,
  loading = false,
  isLastRow = false,
  lastRowIndex,
  isOpen,
  isLocked = false,
  isTierAdjustmentDisabled = false,
  isPriceAdjustmentDisabled = false,
  isPriceModelDisabled,
  keyPrefix = 'products.',
  currency,
  pricesLengthFromApi,
  generateNewPrice,
  handleRemovePrice,
  isReadOnly,
  isBlockPricing: isBlockPricingProp,
}: RatePriceTableRowProps) => {
  const state = useRatePriceTableRowState({
    index,
    product,
    offeringType,
    keyPrefix,
    currency,
    pricesLengthFromApi,
    isBlockPricingProp,
    isLastRow,
  });

  const {
    control,
    getValues,
    setValue,
    clearErrors,
    productId,
    fieldKeyPrefix,
    errorKeyPrefix,
    productError,
    values,
    watchedFrom,
    watchedTo,
    watchedPriceModel,
    watchedAmount,
    watchBlockSize,
    isCustomPriceModel,
    isUsageProductRate,
    isMinCommitProductRate,
    isOneTimePrePaidCreditProductRate,
    isBlockPricing,
    currencySymbol,
    shouldValidateCurrentIndexForDirty,
    showQuantityDropdownWarning,
    dirtyFieldsWithKeyPrefix,
    disabledPriceModelItems,
    priceModelList,
    errorMessageProps,
    isDiscountProductRate,
    handlePriceModel,
    handleTo,
    handleBlockSize,
  } = state;

  /**
   * The Amount input shows a grouped value while it is not focused and an
   * ungrouped one while it is (RATE_AMOUNT_EDIT_FORMAT). Dropping the
   * separators for the duration of the edit keeps them out of the way of
   * MCustomNumberInput's decimal regex, which would otherwise reject every
   * keystroke past the first thousand.
   */
  const [isEditingAmount, setIsEditingAmount] = useState(false);

  const amountInputStyles = useMemo(() => {
    const styles = isReadOnly
      ? READ_ONLY_AMOUNT_INPUT_STYLES
      : AMOUNT_INPUT_STYLES;
    return {
      ...styles,
      pl: getAmountPaddingStart(currencySymbol, AMOUNT_INPUT_PADDING_START),
    };
  }, [currencySymbol, isReadOnly]);

  return (
    <>
      <MTr data-testid="rate-price-table-row" _hover={{ bg: 'transparent' }}>
        {/* Tier Number */}
        <MTd px={2} py={1} verticalAlign="top" w="4">
          <MFlex align="center" minH={8}>
            <MText color="tPurple.base">{`${index + 1}.`}</MText>
          </MFlex>
        </MTd>

        {/* Price Model */}
        <MTd px={2} py={1} verticalAlign="top" maxW="8.5rem">
          <MFormField
            error={productError?.priceModel}
            formErrorMessageProps={errorMessageProps}
          >
            <MLockedTextOrContent
              isLocked={Boolean(isLocked)}
              text={
                OFFERINGS.PRICE_MODEL_OPTIONS[
                  watchedPriceModel as PriceModelEnum
                ]
              }
              maxW="10rem"
              overflow="hidden"
            >
              <Controller
                name={`${fieldKeyPrefix}.${index}.priceModel`}
                defaultValue="TIERED"
                control={control}
                render={({ field: { onChange, ...rest } }) => (
                  <MCustomSelect
                    aria-label="Price model"
                    isDisabled={
                      loading ||
                      isMinCommitProductRate ||
                      isOneTimePrePaidCreditProductRate ||
                      isDiscountProductRate ||
                      isPriceModelDisabled
                    }
                    isReadOnly={isReadOnly}
                    bgColor="tWhite.base"
                    placeholder="Select Pricing Model"
                    items={priceModelList}
                    disabledItems={disabledPriceModelItems}
                    onChange={handlePriceModel(onChange)}
                    renderItemContent={({
                      title,
                      item,
                      isSubtitle,
                      isSelected,
                      isHighlight,
                      isDisabled,
                    }) => (
                      <PriceModelItemContent
                        title={title}
                        item={item}
                        isSubtitle={isSubtitle}
                        isSelected={isSelected}
                        isHighlight={isHighlight}
                        isDisabled={isDisabled}
                        selectedPriceModel={
                          OFFERINGS.PRICE_MODEL_OPTIONS[
                            values[0].priceModel as PriceModelEnum
                          ]
                        }
                      />
                    )}
                    inputProps={{
                      variant: isReadOnly
                        ? 'readonly'
                        : shouldValidateCurrentIndexForDirty &&
                            dirtyFieldsWithKeyPrefix?.[productId]?.prices?.[
                              index
                            ]?.priceModel
                          ? 'unsaved'
                          : 'outline',
                    }}
                    {...rest}
                  />
                )}
              />
            </MLockedTextOrContent>
          </MFormField>
        </MTd>

        {/* From / Minimum Total */}
        <MTd px={2} py={1} verticalAlign="top" maxW="8.5rem">
          <TooltipOrChildren
            isDisabled={isCustomPriceModel}
            label={<DisabledCustomPriceModelLabel />}
          >
            <MFormField
              error={productError?.from}
              formErrorMessageProps={errorMessageProps}
            >
              <MLockedTextOrContent
                text={watchedFrom}
                textType="QUANTITY"
                isLocked={
                  Boolean(isLocked) ||
                  index > 0 ||
                  (isUsageProductRate && index === 0)
                }
              >
                <Controller
                  name={`${fieldKeyPrefix}.${index}.from`}
                  control={control}
                  render={({ field: { onChange, ...rest } }) => (
                    <MCustomNumberInput
                      variant={
                        isReadOnly
                          ? 'readonly'
                          : shouldValidateCurrentIndexForDirty &&
                              dirtyFieldsWithKeyPrefix?.[productId]?.prices?.[
                                index
                              ]?.from
                            ? 'unsaved'
                            : 'outline'
                      }
                      data-testid={`${fieldKeyPrefix}.${index}.from`}
                      aria-label="From"
                      isDisabled={
                        isTierAdjustmentDisabled ||
                        loading ||
                        isCustomPriceModel ||
                        isMinCommitProductRate ||
                        isOneTimePrePaidCreditProductRate ||
                        (isUsageProductRate && index === 0)
                      }
                      isReadOnly={isReadOnly}
                      placeholder="0"
                      onChange={(valueAsString) => onChange(valueAsString)}
                      styleProps={
                        isReadOnly
                          ? READ_ONLY_TIER_INPUT_STYLES
                          : TIER_INPUT_STYLES
                      }
                      {...rest}
                    />
                  )}
                />
              </MLockedTextOrContent>
            </MFormField>
          </TooltipOrChildren>
        </MTd>

        {/* Up to / Maximum Total */}
        <MTd px={2} py={1} verticalAlign="top" maxW="8.5rem">
          <TooltipOrChildren
            isDisabled={isCustomPriceModel}
            label={<DisabledCustomPriceModelLabel />}
          >
            <MFormField
              error={productError?.to}
              formErrorMessageProps={errorMessageProps}
            >
              <MLockedTextOrContent
                text={watchedTo || Infinity}
                textType="QUANTITY"
                isLocked={
                  Boolean(isLocked) ||
                  (isUsageProductRate && index === values.length - 1)
                }
              >
                <Controller
                  name={`${fieldKeyPrefix}.${index}.to`}
                  defaultValue=""
                  control={control}
                  render={({ field: { onChange, ...rest } }) => (
                    <MCustomNumberInput
                      variant={
                        isReadOnly
                          ? 'readonly'
                          : shouldValidateCurrentIndexForDirty &&
                              dirtyFieldsWithKeyPrefix?.[productId]?.prices?.[
                                index
                              ]?.to
                            ? 'unsaved'
                            : 'outline'
                      }
                      data-testid={`${fieldKeyPrefix}.${index}.to`}
                      aria-label="Up to"
                      isDisabled={
                        isTierAdjustmentDisabled ||
                        loading ||
                        isCustomPriceModel ||
                        isMinCommitProductRate ||
                        isOneTimePrePaidCreditProductRate
                      }
                      isReadOnly={isReadOnly}
                      placeholder="∞"
                      styleProps={
                        isReadOnly
                          ? READ_ONLY_TIER_INPUT_STYLES_WITH_PLACEHOLDER
                          : showQuantityDropdownWarning
                            ? TIER_INPUT_STYLES_WITH_WARNING
                            : TIER_INPUT_STYLES_WITH_PLACEHOLDER
                      }
                      onChange={handleTo(onChange)}
                      RightElement={
                        showQuantityDropdownWarning && (
                          <InputElement placement="end" w="4" px="0" pr="2">
                            <MTooltip
                              label={`Tiers without an "Up to" value will not be visible in the quote dropdown`}
                              placement="bottom-start"
                            >
                              <MFlex
                                justifyContent="center"
                                cursor="pointer"
                                pointerEvents="auto"
                              >
                                <Icon
                                  w={4}
                                  h={4}
                                  color="tOrange.tangerine"
                                  asChild
                                >
                                  <MdWarningAmber />
                                </Icon>
                              </MFlex>
                            </MTooltip>
                          </InputElement>
                        )
                      }
                      {...rest}
                    />
                  )}
                />
              </MLockedTextOrContent>
            </MFormField>
          </TooltipOrChildren>
        </MTd>

        {/* Block Size (conditional) */}
        {isBlockPricing && (
          <MTd px={2} py={1} verticalAlign="top" maxW="8.5rem">
            <MFormField
              error={productError?.blockSize}
              formErrorMessageProps={errorMessageProps}
            >
              <MLockedTextOrContent
                text={watchBlockSize}
                textType="QUANTITY"
                isLocked={Boolean(isLocked)}
              >
                <Controller
                  name={`${fieldKeyPrefix}.${index}.blockSize`}
                  defaultValue=""
                  control={control}
                  render={({ field: { onChange, ...rest } }) => (
                    <InputGroup>
                      <MCustomNumberInput
                        variant={
                          isReadOnly
                            ? 'readonly'
                            : shouldValidateCurrentIndexForDirty &&
                                dirtyFieldsWithKeyPrefix?.[productId]?.prices?.[
                                  index
                                ]?.blockSize
                              ? 'unsaved'
                              : 'outline'
                        }
                        data-testid={`${fieldKeyPrefix}.${index}.blockSize`}
                        aria-label="Block Size"
                        isDisabled={
                          isTierAdjustmentDisabled ||
                          loading ||
                          isCustomPriceModel ||
                          isMinCommitProductRate ||
                          isOneTimePrePaidCreditProductRate
                        }
                        min={1}
                        isReadOnly={isReadOnly}
                        placeholder="∞"
                        styleProps={
                          isReadOnly
                            ? READ_ONLY_TIER_INPUT_STYLES_WITH_PLACEHOLDER
                            : TIER_INPUT_STYLES_WITH_PLACEHOLDER
                        }
                        onChange={handleBlockSize(onChange)}
                        {...rest}
                      />
                    </InputGroup>
                  )}
                />
              </MLockedTextOrContent>
            </MFormField>
          </MTd>
        )}

        {/* Amount / Percentage */}
        <MTd px={2} py={1} verticalAlign="top" maxW="8.5rem">
          <TooltipOrChildren
            isDisabled={isCustomPriceModel}
            label={<DisabledCustomPriceModelLabel />}
          >
            <MFormField
              error={productError?.amount}
              formErrorMessageProps={errorMessageProps}
            >
              <MLockedTextOrContent
                isLocked={!!isLocked}
                textType={'CURRENCY'}
                currency={currency}
                text={watchedAmount}
              >
                <Controller
                  name={`${fieldKeyPrefix}.${index}.amount`}
                  control={control}
                  render={({ field: { onChange, onBlur, value, ...rest } }) => (
                    <MCustomNumberInput
                      LeftElement={
                        <InputElement placement="start" w="auto" px="0" pl="2">
                          {currencySymbol}
                        </InputElement>
                      }
                      value={value}
                      formatOptions={
                        isEditingAmount
                          ? RATE_AMOUNT_EDIT_FORMAT
                          : RATE_AMOUNT_DISPLAY_FORMAT
                      }
                      variant={
                        isReadOnly
                          ? 'readonly'
                          : shouldValidateCurrentIndexForDirty &&
                              dirtyFieldsWithKeyPrefix?.[productId]?.prices?.[
                                index
                              ]?.amount
                            ? 'unsaved'
                            : 'outline'
                      }
                      aria-label="Amount"
                      data-testid={`${fieldKeyPrefix}.${index}.amount`}
                      inputMode="decimal"
                      isDisabled={
                        isPriceAdjustmentDisabled ||
                        loading ||
                        isCustomPriceModel ||
                        isMinCommitProductRate ||
                        isOneTimePrePaidCreditProductRate
                      }
                      isReadOnly={isReadOnly}
                      placeholder={`0.00`}
                      styleProps={amountInputStyles}
                      onFocus={() => !isReadOnly && setIsEditingAmount(true)}
                      onBlur={() => {
                        setIsEditingAmount(false);
                        onBlur();
                        if (isReadOnly) {
                          return;
                        }
                        if (value) {
                          const transFormedValue =
                            transformValueToMinimumTwoDecimalPlaces(value);
                          setTimeout(() => onChange(transFormedValue));
                        }
                      }}
                      onChange={(valueAsString) => onChange(valueAsString)}
                      {...rest}
                    />
                  )}
                />
              </MLockedTextOrContent>
            </MFormField>
          </TooltipOrChildren>
        </MTd>

        {/* Delete Button */}
        <MTd px={2} py={1} verticalAlign="top">
          <MFlex alignItems="center" minH={8}>
            {!isLocked && !isReadOnly && index > 0 && (
              <MTooltip label="Delete" placement="bottom-start">
                <MCustomIconButton
                  justifySelf="center"
                  variant="icon"
                  icon={MdClear}
                  aria-label="Delete"
                  containerSize={8}
                  disabled={isTierAdjustmentDisabled || loading}
                  onClick={() => handleRemovePrice(index)}
                />
              </MTooltip>
            )}
          </MFlex>
        </MTd>
      </MTr>

      {/* Add Tier Button Row */}
      {!isTierAdjustmentDisabled &&
        !isLocked &&
        !isCustomPriceModel &&
        isOpen &&
        isLastRow &&
        !isOneTimePrePaidCreditProductRate &&
        !isMinCommitProductRate &&
        !isReadOnly && (
          <MTr _hover={{ bg: 'transparent' }}>
            <MTd colSpan={isBlockPricing ? 7 : 6} px={2} py={1} border="none">
              <MFlex mt={2} alignItems="center">
                {isUsageProductRate &&
                  isLastRow &&
                  getValues(`${fieldKeyPrefix}.${lastRowIndex}.to`) && (
                    <MText mr="2">
                      {
                        'The final tier must have infinity as the "Up to" value.'
                      }
                    </MText>
                  )}
                {watchedPriceModel !== PriceModelEnum.BLOCK && (
                  <MButton
                    size="sm"
                    variant="tertiary"
                    fontWeight="600"
                    isDisabled={isLocked || loading}
                    onClick={() => {
                      let newToValue = 1;
                      if (
                        isNumber(Number(watchedFrom)) &&
                        !isNaN(watchedFrom)
                      ) {
                        newToValue = Number(watchedFrom) + 1;
                      }

                      if (!watchedTo) {
                        setValue(`${fieldKeyPrefix}.${index}.to`, newToValue, {
                          shouldDirty: true,
                        });
                        if (productError?.to) {
                          clearErrors(`${errorKeyPrefix}.to`);
                        }
                      }
                      generateNewPrice(
                        parseInt(watchedFrom, 10),
                        parseInt(!watchedTo ? newToValue : watchedTo, 10),
                        watchedPriceModel,
                        watchedAmount,
                        watchBlockSize,
                      );
                    }}
                  >
                    + Add Tier
                  </MButton>
                )}
              </MFlex>
            </MTd>
          </MTr>
        )}
    </>
  );
};

// ============================================================================
// POT Variant (PoT)
// ============================================================================
const RatePriceTableRowPoT = ({
  index,
  product,
  offeringType,
  loading = false,
  isLastRow = false,
  isPriceDrawer,
  isLocked = false,
  isPriceAdjustmentDisabled = false,
  isPriceModelDisabled,
  keyPrefix = 'products.',
  currency,
  pricesLengthFromApi,
  isReadOnly,
  isBlockPricing: isBlockPricingProp,
}: RatePriceTableRowProps) => {
  const state = useRatePriceTableRowState({
    index,
    product,
    offeringType,
    keyPrefix,
    currency,
    pricesLengthFromApi,
    isBlockPricingProp,
    isLastRow,
  });

  const {
    control,
    errors,
    setError,
    clearErrors,
    productId,
    fieldKeyPrefix,
    productError,
    values,
    watchedPriceModel,
    watchedAmount,
    isCustomPriceModel,
    isUsageProductRate,
    isMinCommitProductRate,
    isOneTimePrePaidCreditProductRate,
    shouldValidateCurrentIndexForDirty,
    dirtyFieldsWithKeyPrefix,
    handlePriceModel,
    disabledPriceModelItems,
    priceModelList,
    errorMessageProps,
    isDiscountProductRate,
    isPoTOfferingType,
    watchPercentOfTotalConfig,
  } = state;

  return (
    <MTr data-testid="rate-price-table-row" _hover={{ bg: 'transparent' }}>
      {/* Tier Number */}
      <MTd px={2} py={1} verticalAlign="top" w="4">
        <MFlex align="center" minH={8}>
          <MText color="tPurple.base">{`${index + 1}.`}</MText>
        </MFlex>
      </MTd>

      {/* Price Model */}
      <MTd px={2} py={1} verticalAlign="top" maxW="10rem">
        <MFormField
          error={productError?.priceModel}
          formErrorMessageProps={errorMessageProps}
        >
          <MLockedTextOrContent
            isLocked={Boolean(isLocked)}
            text={
              OFFERINGS.PRICE_MODEL_OPTIONS[watchedPriceModel as PriceModelEnum]
            }
            maxW="10rem"
            overflow="hidden"
          >
            <Controller
              name={`${fieldKeyPrefix}.${index}.priceModel`}
              defaultValue="TIERED"
              control={control}
              render={({ field: { onChange, ...rest } }) => (
                <MCustomSelect
                  aria-label="Price model"
                  isDisabled={
                    loading ||
                    isMinCommitProductRate ||
                    isOneTimePrePaidCreditProductRate ||
                    isDiscountProductRate ||
                    isPoTOfferingType ||
                    isPriceModelDisabled
                  }
                  isReadOnly={isReadOnly}
                  bgColor="tWhite.base"
                  placeholder="Select Pricing Model"
                  items={priceModelList}
                  disabledItems={disabledPriceModelItems}
                  onChange={handlePriceModel(onChange)}
                  renderItemContent={({
                    title,
                    item,
                    isSubtitle,
                    isSelected,
                    isHighlight,
                    isDisabled,
                  }) => (
                    <PriceModelItemContent
                      title={title}
                      item={item}
                      isSubtitle={isSubtitle}
                      isSelected={isSelected}
                      isHighlight={isHighlight}
                      isDisabled={isDisabled}
                      selectedPriceModel={
                        OFFERINGS.PRICE_MODEL_OPTIONS[
                          values[0].priceModel as PriceModelEnum
                        ]
                      }
                    />
                  )}
                  inputProps={{
                    variant: isReadOnly
                      ? 'readonly'
                      : shouldValidateCurrentIndexForDirty &&
                          dirtyFieldsWithKeyPrefix?.[productId]?.prices?.[index]
                            ?.priceModel
                        ? 'unsaved'
                        : 'outline',
                  }}
                  {...rest}
                />
              )}
            />
          </MLockedTextOrContent>
        </MFormField>
      </MTd>

      {/* From / Minimum Total */}

      <MTd px={2} py={1} verticalAlign="top" w="8.5rem">
        <MFormField
          error={(errors.percentOfTotalConfig as any)?.minimumTotal}
          formErrorMessageProps={errorMessageProps}
        >
          <MLockedTextOrContent
            text={watchPercentOfTotalConfig.minimumTotal || ''}
            textType="QUANTITY"
            isLocked={
              Boolean(isLocked) ||
              index > 0 ||
              (isUsageProductRate && index === 0)
            }
          >
            <Controller
              name={'percentOfTotalConfig.minimumTotal'}
              control={control}
              render={({ field: { onChange, ...rest } }) => (
                <MCustomNumberInput
                  data-testid={`${fieldKeyPrefix}-${index}-minimumTotal`}
                  aria-label="Minimum Total"
                  isDisabled={isPriceAdjustmentDisabled || loading}
                  isReadOnly={isReadOnly}
                  placeholder="∞"
                  onChange={(valueAsString, valueAsNumber) => {
                    clearErrors(`percentOfTotalConfig.minimumTotal`);
                    if (valueAsString === '') {
                      onChange(null);
                    } else {
                      onChange(valueAsNumber);
                      if (
                        watchPercentOfTotalConfig.maximumTotal !== null &&
                        valueAsNumber > watchPercentOfTotalConfig.maximumTotal
                      ) {
                        setError(`percentOfTotalConfig.minimumTotal`, {
                          type: 'validate',
                          message: `Minimum Total must be less than or equal to Maximum Total`,
                        });
                      }
                    }
                  }}
                  styleProps={
                    isReadOnly ? READ_ONLY_TIER_INPUT_STYLES : TIER_INPUT_STYLES
                  }
                  {...rest}
                />
              )}
            />
          </MLockedTextOrContent>
        </MFormField>
      </MTd>

      {/* Up to / Maximum Total */}
      <MTd px={2} py={1} verticalAlign="top" w="8.5rem">
        <MFormField
          error={(errors.percentOfTotalConfig as any)?.maximumTotal}
          formErrorMessageProps={errorMessageProps}
        >
          <MLockedTextOrContent
            text={watchPercentOfTotalConfig.maximumTotal || Infinity}
            textType="QUANTITY"
            isLocked={
              Boolean(isLocked) ||
              (isUsageProductRate && index === values.length - 1)
            }
          >
            <Controller
              name={'percentOfTotalConfig.maximumTotal'}
              defaultValue=""
              control={control}
              render={({ field: { onChange, ...rest } }) => (
                <MCustomNumberInput
                  data-testid={`${fieldKeyPrefix}-${index}-maximumTotal`}
                  aria-label="Maximum Total"
                  isDisabled={isPriceAdjustmentDisabled || loading}
                  isReadOnly={isReadOnly}
                  placeholder="∞"
                  styleProps={
                    isReadOnly
                      ? READ_ONLY_TIER_INPUT_STYLES_WITH_PLACEHOLDER
                      : TIER_INPUT_STYLES_WITH_PLACEHOLDER
                  }
                  onChange={(valueAsString, valueAsNumber) => {
                    clearErrors(`percentOfTotalConfig.maximumTotal`);
                    if (valueAsString === '') {
                      onChange(null);
                    } else {
                      onChange(valueAsNumber);
                      if (
                        valueAsNumber < watchPercentOfTotalConfig.minimumTotal
                      ) {
                        setError(`percentOfTotalConfig.maximumTotal`, {
                          type: 'validate',
                          message: `Maximum Total must be greater than or equal to Minimum Total`,
                        });
                      }
                    }
                  }}
                  {...rest}
                />
              )}
            />
          </MLockedTextOrContent>
        </MFormField>
      </MTd>

      {/* Amount / Percentage */}
      <MTd px={2} py={1} verticalAlign="top" w="8.5rem">
        <TooltipOrChildren
          isDisabled={isCustomPriceModel}
          label={<DisabledCustomPriceModelLabel />}
        >
          <MFormField
            error={productError?.amount}
            formErrorMessageProps={errorMessageProps}
          >
            <MLockedTextOrContent
              isLocked={!!isLocked}
              textType={'PERCENTAGE'}
              currency={currency}
              text={watchedAmount ? watchedAmount / 100 : watchedAmount}
            >
              <Controller
                name={`${fieldKeyPrefix}.${index}.amount`}
                control={control}
                render={({ field: { onChange, onBlur, ...rest } }) => (
                  <MCustomNumberInput
                    variant={
                      isReadOnly
                        ? 'readonly'
                        : shouldValidateCurrentIndexForDirty &&
                            dirtyFieldsWithKeyPrefix?.[productId]?.prices?.[
                              index
                            ]?.amount
                          ? 'unsaved'
                          : 'outline'
                    }
                    aria-label="Amount"
                    data-testid={`${fieldKeyPrefix}.${index}.amount`}
                    inputMode="decimal"
                    isDisabled={
                      isPriceAdjustmentDisabled ||
                      loading ||
                      isCustomPriceModel ||
                      isMinCommitProductRate ||
                      isOneTimePrePaidCreditProductRate
                    }
                    isReadOnly={isReadOnly}
                    placeholder={`0.00`}
                    styleProps={
                      isReadOnly
                        ? READ_ONLY_POT_AMOUNT_INPUT_STYLES
                        : POT_AMOUNT_INPUT_STYLES
                    }
                    onBlur={(e: FocusEvent<HTMLInputElement>) => {
                      onBlur();
                      const { value } = e.target;
                      if (value) {
                        const transFormedValue =
                          transformValueToMinimumTwoDecimalPlaces(value);
                        setTimeout(() => onChange(transFormedValue));
                      }
                    }}
                    onChange={(valueAsString) => onChange(valueAsString)}
                    RightElement={
                      <InputElement placement="end" w="4" px="0" pr="2">
                        %
                      </InputElement>
                    }
                    {...rest}
                  />
                )}
              />
            </MLockedTextOrContent>
          </MFormField>
        </TooltipOrChildren>
      </MTd>

      {index === 0 && !isPriceDrawer && (
        <MTd px={2} py={1} verticalAlign="top">
          <RatePercentOfTotalConfig
            product={product}
            isLoading={loading}
            isLocked={isLocked || isReadOnly}
          />
        </MTd>
      )}
    </MTr>
  );
};

export default RatePriceTableRow;
